/**
 * Client error reporting for the public website and the gated /portal/ pages.
 *
 * Loaded synchronously at the top of <head> (base.html, base_sub_contractor.html)
 * so its handlers are in place before any other script runs. Every error the
 * page catches becomes one POST /api/client-errors/report/ — the same intake
 * the portal SPA and the app use, with `platform: "website"` — carrying the
 * error, where it happened, the last few things the visitor did, and what the
 * browser is. The server keeps every report and groups them by fingerprint
 * (see docs/api/client-error-reporting.md).
 *
 * What is caught:
 *   - uncaught exceptions (window `error`) and unhandled promise rejections;
 *   - assets that failed to load (a <script>/<link>/<img> `error` in the capture
 *     phase) — after a deploy this is how a stale page asking for a file that
 *     no longer exists shows up. Three or more failing inside 1.5 s go as one
 *     report, and nothing is sent while the page is unloading or offline;
 *     Cloudflare's /cdn-cgi/ scripts and blocked analytics hosts are skipped;
 *   - same-origin `fetch` calls that answered 5xx or never answered. A 4xx is
 *     the server saying no and the form already handles it. Third-party hosts
 *     (analytics, maps) are not ours to report;
 *   - anything a page script hands to `window.reportClientError(error, opts)`.
 *
 * Rules enforced here so page scripts do not have to think about them: it
 * never throws; the same error goes once per 30 s and at most 20 reports leave
 * per minute (the server throttles at 120/min as a backstop); a failed send is
 * queued and retried when the tab is back online, and whatever is still queued
 * when the tab closes goes out with sendBeacon. Anonymous by design — the
 * website has no user session — and nothing typed into a field is ever put in
 * a breadcrumb.
 *
 * Page API (all safe to call before anything else has loaded):
 *   window.reportClientError(error, { source, severity, context, tags })
 *   window.addErrorBreadcrumb({ category, message, data })
 */
(function () {
    'use strict';

    var ENDPOINT = '/api/client-errors/report/';
    var PLATFORM = 'website';
    var DEDUPE_WINDOW_MS = 30000;
    var MAX_PER_MINUTE = 20;
    var MAX_BREADCRUMBS = 50;
    var MAX_QUEUED = 20;
    var RETRY_DELAY_MS = 30000;
    var MESSAGE_MAX = 10000;
    var STACK_MAX = 50000;
    var RESPONSE_BODY_MAX = 4000;
    var SESSION_KEY = 'shining.error_reporting.session.v1';
    var ASSET_BURST_MS = 1500;
    var ASSET_BURST_THRESHOLD = 3;

    var breadcrumbs = [];
    var failedAssets = [];
    var assetFlushTimer = null;
    var pageUnloading = false;
    var recentByKey = {};
    var sentTimestamps = [];
    var queue = [];
    var retryTimer = null;
    var sessionStartedAt = Date.now();

    // ------------------------------------------------------------------
    // helpers
    // ------------------------------------------------------------------

    function clip(value, max) {
        value = value == null ? '' : String(value);
        return value.length > max ? value.slice(0, max - 1) + '…' : value;
    }

    function randomId() {
        try {
            if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
        } catch (e) { /* fall through */ }
        return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }

    function sessionId() {
        try {
            var existing = window.sessionStorage.getItem(SESSION_KEY);
            if (existing) {
                var parsed = JSON.parse(existing);
                sessionStartedAt = parsed.started_at || sessionStartedAt;
                return parsed.id;
            }
            var fresh = { id: randomId(), started_at: sessionStartedAt };
            window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(fresh));
            return fresh.id;
        } catch (e) {
            return '';
        }
    }

    function stringify(value) {
        if (typeof value === 'string') return value;
        try {
            var json = JSON.stringify(value);
            if (json && json !== '{}') return json;
        } catch (e) { /* circular */ }
        return String(value);
    }

    /** Reduce whatever was thrown to name / message / stack. */
    function normaliseError(error) {
        if (error instanceof Error) {
            return { name: error.name || 'Error', message: error.message || String(error), stack: error.stack || '' };
        }
        if (error && typeof error === 'object') {
            if (typeof error.message === 'string') {
                return {
                    name: typeof error.name === 'string' ? error.name : 'Error',
                    message: error.message,
                    stack: typeof error.stack === 'string' ? error.stack : ''
                };
            }
            return { name: 'Error', message: clip(stringify(error), 2000), stack: '' };
        }
        return { name: 'Error', message: error === undefined ? 'Unknown error' : String(error), stack: '' };
    }

    var UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    var TOKEN_SEGMENT = /^[A-Za-z0-9_-]{24,}$/; // signed links: /portal/quote/<token>/
    var NUMERIC_SEGMENT = /^\d+$/;

    /** `/blog/123/` and `/portal/quote/<token>/` become one route each. */
    function normaliseRoute(pathname) {
        return pathname.split('/').map(function (segment) {
            return UUID_SEGMENT.test(segment) || NUMERIC_SEGMENT.test(segment) || TOKEN_SEGMENT.test(segment)
                ? ':id' : segment;
        }).join('/');
    }

    function sameOrigin(url) {
        try {
            return new URL(String(url), window.location.href).origin === window.location.origin;
        } catch (e) {
            return false;
        }
    }

    function isReportUrl(url) {
        return String(url).indexOf(ENDPOINT) !== -1;
    }

    // Analytics and tag hosts. Ad blockers and privacy browsers refuse these on
    // a large share of visits, the page works without them, and a report for
    // each would drown out the failed assets a deploy can actually break.
    // Functional third parties (Google Maps, the Lucide CDN, Google sign-in)
    // are deliberately not listed — when one of those fails the page is broken.
    var IGNORED_ASSET_HOSTS = [
        'clarity.ms',
        'posthog.com',
        'googletagmanager.com',
        'google-analytics.com',
        'doubleclick.net',
        'facebook.net'
    ];

    /**
     * Failed assets not worth a report: the analytics hosts above, and
     * `/cdn-cgi/…`, which is Cloudflare's, injected at the edge (email-decode,
     * Rocket Loader, challenge scripts, Insights beacon) rather than written in
     * our templates, and blocked by extensions and crawlers just as often.
     */
    function isIgnoredAssetUrl(url) {
        try {
            var parsed = new URL(String(url), window.location.href);
            if (parsed.pathname.indexOf('/cdn-cgi/') === 0) return true;
            var host = parsed.hostname;
            return IGNORED_ASSET_HOSTS.some(function (suffix) {
                return host === suffix || host.slice(-(suffix.length + 1)) === '.' + suffix;
            });
        } catch (e) {
            return false;
        }
    }

    // ------------------------------------------------------------------
    // breadcrumbs
    // ------------------------------------------------------------------

    function addBreadcrumb(crumb) {
        if (!crumb || !crumb.message) return;
        breadcrumbs.push({
            timestamp: new Date().toISOString(),
            category: crumb.category || 'log',
            message: clip(crumb.message, 300),
            data: crumb.data || undefined
        });
        if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.splice(0, breadcrumbs.length - MAX_BREADCRUMBS);
    }

    /**
     * A short label for the clicked element: tag, then whatever names it best.
     * Never the element's value — a click on an input must not log what was
     * typed into it.
     */
    function describeClickTarget(target) {
        if (!target || !target.tagName || typeof target.closest !== 'function') return null;
        var el = target.closest('button, a, [role="button"], [role="menuitem"], [role="tab"], input, select, textarea, label, summary') || target;
        var tag = el.tagName.toLowerCase();
        var isField = tag === 'input' || tag === 'select' || tag === 'textarea';
        var name = el.getAttribute('aria-label') || el.getAttribute('data-testid') || el.getAttribute('name') || el.id ||
            (isField ? el.getAttribute('placeholder') : (el.textContent || '').replace(/\s+/g, ' ').trim());
        var label = clip(name || '', 60);
        return label ? tag + ' "' + label + '"' : tag;
    }

    // ------------------------------------------------------------------
    // environment
    // ------------------------------------------------------------------

    function environment() {
        var nav = window.navigator || {};
        var conn = nav.connection || {};
        var perf = window.performance || {};
        var heap = perf.memory && perf.memory.usedJSHeapSize;
        var timezone = '';
        try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { /* unavailable */ }
        var host = window.location.hostname;
        var isLocal = host === 'localhost' || host === '127.0.0.1' || /\.local$/.test(host);
        return {
            url: window.location.href,
            route: normaliseRoute(window.location.pathname),
            referrer: document.referrer || '',
            session_id: sessionId(),
            environment: isLocal ? 'development' : 'production',
            screen: window.screen ? window.screen.width + 'x' + window.screen.height + '@' + (window.devicePixelRatio || 1) : '',
            viewport: window.innerWidth + 'x' + window.innerHeight,
            locale: nav.language || '',
            timezone: timezone,
            network_type: conn.effectiveType || conn.type || '',
            is_online: typeof nav.onLine === 'boolean' ? nav.onLine : null,
            memory_mb: typeof heap === 'number' ? Math.round(heap / 1048576) : null,
            session_duration_seconds: Math.max(0, Math.round((Date.now() - sessionStartedAt) / 1000))
        };
    }

    // ------------------------------------------------------------------
    // payload + rate limiting
    // ------------------------------------------------------------------

    function assign(target, source) {
        for (var key in source) if (Object.prototype.hasOwnProperty.call(source, key)) target[key] = source[key];
        return target;
    }

    function buildReport(error, opts) {
        opts = opts || {};
        var normalised = normaliseError(error);
        var payload = {
            platform: PLATFORM,
            severity: opts.severity || 'error',
            source: opts.source || 'manual',
            error_name: clip(normalised.name, 200),
            message: clip(normalised.message, MESSAGE_MAX),
            stack: clip(normalised.stack, STACK_MAX),
            handled: opts.handled !== undefined ? !!opts.handled : true,
            client_event_id: randomId(),
            occurred_at: new Date().toISOString(),
            breadcrumbs: breadcrumbs.slice(),
            tags: opts.tags || {},
            context: opts.context || {}
        };
        assign(payload, environment());
        if (opts.request) {
            payload.request_method = String(opts.request.method || '').toUpperCase();
            payload.request_url = opts.request.url || '';
            payload.response_status = opts.request.status == null ? null : opts.request.status;
            payload.response_body = clip(opts.request.responseBody || '', RESPONSE_BODY_MAX);
        }
        return payload;
    }

    function dedupeKey(normalised) {
        var lines = normalised.stack.split('\n');
        var frame = '';
        for (var i = 0; i < lines.length; i += 1) {
            if (/\bat\b|@/.test(lines[i])) { frame = lines[i].trim(); break; }
        }
        return normalised.name + '|' + normalised.message.slice(0, 200) + '|' + frame.slice(0, 200);
    }

    function shouldSend(normalised, now) {
        var key = dedupeKey(normalised);
        if (recentByKey[key] !== undefined && now - recentByKey[key] < DEDUPE_WINDOW_MS) return false;
        while (sentTimestamps.length && now - sentTimestamps[0] > 60000) sentTimestamps.shift();
        if (sentTimestamps.length >= MAX_PER_MINUTE) return false;
        recentByKey[key] = now;
        sentTimestamps.push(now);
        return true;
    }

    // ------------------------------------------------------------------
    // sending
    // ------------------------------------------------------------------

    var nativeFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;

    function deliver(payload) {
        if (!nativeFetch) return Promise.resolve(false);
        try {
            return nativeFetch(ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'omit',
                keepalive: true
            }).then(function (response) {
                return response.ok;
            }, function () {
                return false;
            });
        } catch (e) {
            return Promise.resolve(false);
        }
    }

    function scheduleRetry() {
        if (retryTimer || !queue.length) return;
        retryTimer = setTimeout(function () {
            retryTimer = null;
            flushQueue();
        }, RETRY_DELAY_MS);
    }

    function flushQueue() {
        if (!queue.length || window.navigator.onLine === false) { scheduleRetry(); return; }
        var pending = queue.splice(0, queue.length);
        pending.forEach(function (payload) {
            deliver(payload).then(function (ok) {
                if (!ok && queue.length < MAX_QUEUED) queue.push(payload);
                scheduleRetry();
            });
        });
    }

    function flushOnUnload() {
        if (!queue.length || !window.navigator || typeof window.navigator.sendBeacon !== 'function') return;
        queue.splice(0, queue.length).forEach(function (payload) {
            try {
                window.navigator.sendBeacon(ENDPOINT, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
            } catch (e) { /* on the way out */ }
        });
    }

    /** Report an error. Returns the payload queued for sending, or null. Never throws. */
    function report(error, opts) {
        try {
            var normalised = normaliseError(error);
            if (!shouldSend(normalised, Date.now())) return null;
            var payload = buildReport(error, opts);
            deliver(payload).then(function (ok) {
                if (!ok) {
                    if (queue.length < MAX_QUEUED) queue.push(payload);
                    scheduleRetry();
                }
            });
            return payload;
        } catch (e) {
            return null;
        }
    }

    // ------------------------------------------------------------------
    // global handlers
    // ------------------------------------------------------------------

    function onWindowError(event) {
        if (event && typeof event.message === 'string' && (event.error || event.filename || event.lineno)) {
            report(event.error || event.message, {
                source: 'window_error',
                severity: 'error',
                handled: false,
                context: { filename: event.filename, lineno: event.lineno, colno: event.colno }
            });
            return;
        }
        var target = event && event.target;
        if (!target || target === window || !target.tagName) return;
        var src = target.src || target.href;
        if (!src || isIgnoredAssetUrl(src)) return;
        // Leaving the page aborts whatever is still loading, and offline
        // everything fails — neither says anything about the asset.
        if (pageUnloading || window.navigator.onLine === false) return;
        failedAssets.push(target.tagName.toLowerCase() + ' ' + src);
        if (!assetFlushTimer) assetFlushTimer = setTimeout(flushFailedAssets, ASSET_BURST_MS);
    }

    /**
     * One failed asset is reported as itself. Several failing inside the same
     * window is the visitor's connection, an extension or a crawler refusing
     * images, not a dozen broken files — that goes as one report listing them,
     * so it groups under one fingerprint instead of one per URL.
     */
    function flushFailedAssets() {
        assetFlushTimer = null;
        var failed = failedAssets.splice(0, failedAssets.length);
        if (!failed.length || pageUnloading) return;
        if (failed.length < ASSET_BURST_THRESHOLD) {
            failed.forEach(function (asset) {
                report(new Error('Failed to load ' + asset), { source: 'resource_error', severity: 'warning', handled: false });
            });
            return;
        }
        report(new Error('Several assets failed to load at once'), {
            source: 'resource_error',
            severity: 'warning',
            handled: false,
            context: { failed_count: failed.length, assets: failed.slice(0, 30) }
        });
    }

    function onUnhandledRejection(event) {
        report(event && event.reason, { source: 'unhandled_rejection', severity: 'error', handled: false });
    }

    function onClick(event) {
        var label = describeClickTarget(event.target);
        if (label) addBreadcrumb({ category: 'ui.click', message: label });
    }

    /**
     * Same-origin fetch calls leave an `http` breadcrumb and report a 5xx or a
     * call that never came back. The response body is read from a clone so the
     * caller still gets theirs.
     */
    function wrapFetch() {
        if (!nativeFetch) return;
        window.fetch = function (input, init) {
            var url = typeof input === 'string' ? input : (input && input.url) || '';
            var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
            var ours = sameOrigin(url) && !isReportUrl(url);
            var path = ours ? normaliseRoute(new URL(url, window.location.href).pathname) : url;
            var startedAt = Date.now();
            var call = nativeFetch(input, init);
            if (!ours) return call;
            return call.then(function (response) {
                addBreadcrumb({
                    category: 'http',
                    message: method + ' ' + path + ' ' + response.status,
                    data: { method: method, url: path, status: response.status, duration_ms: Date.now() - startedAt }
                });
                if (response.status >= 500) {
                    var reportWith = function (body) {
                        var err = new Error(method + ' ' + path + ' → ' + response.status);
                        err.name = 'ApiError';
                        report(err, {
                            source: 'api_error',
                            severity: 'error',
                            handled: true,
                            request: { method: method, url: path, status: response.status, responseBody: body }
                        });
                    };
                    try {
                        response.clone().text().then(reportWith, function () { reportWith(''); });
                    } catch (e) {
                        reportWith('');
                    }
                }
                return response;
            }, function (error) {
                addBreadcrumb({
                    category: 'http',
                    message: method + ' ' + path + ' network error',
                    data: { method: method, url: path, status: null, duration_ms: Date.now() - startedAt }
                });
                var aborted = error && error.name === 'AbortError';
                if (!aborted) {
                    var err = new Error(method + ' ' + path + ' → no response: ' + (error && error.message));
                    err.name = 'ApiNetworkError';
                    err.stack = error && error.stack;
                    report(err, {
                        source: 'api_error',
                        severity: 'error',
                        handled: true,
                        request: { method: method, url: path, status: null }
                    });
                }
                throw error;
            });
        };
    }

    function install() {
        if (window.__shiningErrorReportingInstalled) return;
        window.__shiningErrorReportingInstalled = true;
        window.addEventListener('error', onWindowError, true);
        window.addEventListener('unhandledrejection', onUnhandledRejection);
        window.addEventListener('online', flushQueue);
        // beforeunload also fires for mailto:/tel: links and cancelled
        // navigations, which leave the page open — so the flag clears itself.
        window.addEventListener('beforeunload', function () {
            pageUnloading = true;
            setTimeout(function () { pageUnloading = false; }, 3000);
        });
        window.addEventListener('pagehide', function () { pageUnloading = true; flushOnUnload(); });
        window.addEventListener('pageshow', function () { pageUnloading = false; });
        document.addEventListener('click', onClick, true);
        wrapFetch();
        addBreadcrumb({
            category: 'navigation',
            message: normaliseRoute(window.location.pathname),
            data: { referrer: document.referrer || '' }
        });
    }

    window.reportClientError = function (error, opts) { return report(error, opts); };
    window.addErrorBreadcrumb = addBreadcrumb;
    // Exposed for the smoke test; not a page API.
    window.__shiningErrorReporting = { normaliseRoute: normaliseRoute, describeClickTarget: describeClickTarget, buildReport: buildReport, flushQueue: flushQueue, flushOnUnload: flushOnUnload };

    install();
})();
